import React, { useState } from 'react';
import { escapeHtml } from '@/utils/htmlEscape';
import { formatNoteText } from '@/utils/formatNoteText';
import { formatClientFieldList } from '@/lib/clientFieldLabels';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { logActivity } from '@/hooks/useActivityLog';
import { useBrandingContext } from '@/contexts/BrandingContext';
import { getEffectiveRole } from '@/constants/roles';
import type { AppRole } from '@/constants/roles';
import { ShiftDetailsDialog } from '@/components/staff/ShiftDetailsDialog';
import { useShiftReportTemplate } from '@/hooks/useShiftReportTemplate';
import { useShiftConfig } from '@/hooks/useShiftConfig';
import { DAILY_REPORT_FRAME, DEFAULT_SHIFT_FRAMES, formatShiftLabel, getCurrentShiftSelection, getShiftBounds } from '@/utils/shiftConfig';
import {
  DEFAULT_SHIFT_REPORT_TEMPLATE,
  createEmptyShiftReportValues,
  getEnabledShiftReportSections,
  type ShiftReportValues,
} from '@/utils/shiftReportTemplate';
import {
  Home, Calendar as CalendarIcon, Train, Shirt,
  UtensilsCrossed, CupSoda, AlertTriangle, LogIn, FileText,
  UserCheck, UserX, Activity, Plus, UserPlus, DoorOpen,
  Printer, Download, Maximize2, Bed, CalendarCheck2, Archive,
  ClipboardList, HeartPulse
} from 'lucide-react';

type ShiftType = string;

// --- Shift Details (agency-configurable manual report values) ---
type ShiftDetails = ShiftReportValues;

const shiftDetailsStorageKey = (date: Date, shift: ShiftType, organizationId?: string | null) =>
  `shift-report-details-${organizationId || 'org'}-${format(date, 'yyyy-MM-dd')}-${shift}`;

interface TimelineEvent {
  id: string;
  time: string;
  type: 'service' | 'alert' | 'login' | 'activity' | 'manual';
  category?: string;
  icon: React.ReactNode;
  title: string;
  description?: string;
  badgeVariant?: 'default' | 'destructive' | 'secondary' | 'outline';
  createdBy?: string;
  actorName?: string;
  sourceId?: string;
  sourceType?: 'note' | 'alert';
  caseManagerNotified?: boolean;
  clientName?: string;
}

interface ShiftReportProps {
  onBack: () => void;
}

const ShiftReport: React.FC<ShiftReportProps> = ({ onBack }) => {
  const initialShiftSelection = React.useMemo(() => getCurrentShiftSelection(DEFAULT_SHIFT_FRAMES), []);
  const [selectedDate, setSelectedDate] = useState<Date>(initialShiftSelection.date);
  const [dateOpen, setDateOpen] = useState(false);
  const [shift, setShift] = useState<ShiftType>(initialShiftSelection.shift.id);
  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [entryType, setEntryType] = useState<string>('note');
  const [entryTitle, setEntryTitle] = useState('');
  const [entryDescription, setEntryDescription] = useState('');
  const [entryDate, setEntryDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [entryTime, setEntryTime] = useState<string>(format(new Date(), 'HH:mm'));
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [editingEvent, setEditingEvent] = useState<TimelineEvent | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [shiftDetailsOpen, setShiftDetailsOpen] = useState(false);
  const [shiftDetails, setShiftDetails] = useState<ShiftDetails>({});
  const queryClient = useQueryClient();
  const { isDemoMode, user, organizationId, userSites } = useAuth();
  const { organizationName } = useBrandingContext();
  const { data: configuredShifts = DEFAULT_SHIFT_FRAMES } = useShiftConfig();
  const { data: shiftReportTemplate = DEFAULT_SHIFT_REPORT_TEMPLATE } = useShiftReportTemplate();

  const userRole = user?.role as AppRole | undefined;
  const effectiveRole = getEffectiveRole(userRole);
  const canEditAll = effectiveRole === 'SUPER_ADMIN' || effectiveRole === 'MANAGER';
  const currentUserId = user?.id;

  const enabledReportSections = getEnabledShiftReportSections(shiftReportTemplate);
  const emptyReportDetails = React.useCallback(() => createEmptyShiftReportValues(shiftReportTemplate), [shiftReportTemplate]);
  const reportOptions = React.useMemo(() => [...configuredShifts, DAILY_REPORT_FRAME], [configuredShifts]);
  const selectedShift = reportOptions.find((item) => item.id === shift) || reportOptions[0] || DEFAULT_SHIFT_FRAMES[0];
  const shiftLabel = formatShiftLabel(selectedShift);
  React.useEffect(() => {
    const current = getCurrentShiftSelection(configuredShifts);
    setSelectedDate(current.date);
    setShift(current.shift.id);
  }, [configuredShifts]);
  React.useEffect(() => {
    if (!reportOptions.some((item) => item.id === shift)) {
      setShift(getCurrentShiftSelection(configuredShifts).shift.id);
    }
  }, [configuredShifts, reportOptions, shift]);

  const bounds = getShiftBounds(selectedDate, selectedShift);
  const startISO = bounds.start.toISOString();
  const endISO = bounds.end.toISOString();

  // Resolve a site_id for persisting/loading shift report values.
  // Falls back to localStorage if user has no assigned site (org-only context).
  const reportSiteId: string | null = (userSites && userSites[0]) || null;

  // Load shift details — DB first (with localStorage backfill), fall back to localStorage
  React.useEffect(() => {
    let cancelled = false;
    const lsKey = shiftDetailsStorageKey(selectedDate, shift, organizationId);
    const loadFromLocal = () => {
      try {
        const raw = localStorage.getItem(lsKey);
        if (!cancelled) setShiftDetails(raw ? { ...emptyReportDetails(), ...JSON.parse(raw) } : emptyReportDetails());
      } catch {
        if (!cancelled) setShiftDetails(emptyReportDetails());
      }
    };

    (async () => {
      if (!reportSiteId) {
        loadFromLocal();
        return;
      }
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const { data, error } = await supabase
        .from('shift_report_entries')
        .select('values')
        .eq('site_id', reportSiteId)
        .eq('report_date', dateStr)
        .eq('shift_id', shift)
        .maybeSingle();
      if (cancelled) return;
      if (!error && data?.values) {
        setShiftDetails({ ...emptyReportDetails(), ...(data.values as ShiftDetails) });
      } else {
        // Backfill from localStorage if present
        loadFromLocal();
      }
    })();

    return () => { cancelled = true; };
  }, [selectedDate, shift, organizationId, emptyReportDetails, reportSiteId]);

  const saveShiftDetails = async (details: ShiftDetails) => {
    try {
      const manualDetails = Object.fromEntries(
        Object.entries(details).filter(([key]) => !serviceTotalFieldIds.has(key))
      ) as ShiftDetails;

      // Always cache locally for offline/print resilience
      localStorage.setItem(shiftDetailsStorageKey(selectedDate, shift, organizationId), JSON.stringify(manualDetails));

      if (reportSiteId) {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const { error } = await supabase
          .from('shift_report_entries')
          .upsert(
            {
              site_id: reportSiteId,
              report_date: dateStr,
              shift_id: shift,
              values: manualDetails as any,
              submitted_by: currentUserId ?? null,
            },
            { onConflict: 'site_id,report_date,shift_id' }
          );
        if (error) throw error;
        queryClient.invalidateQueries({ queryKey: ['shift-report-totals'] });
      }

      setShiftDetails(manualDetails);
      setShiftDetailsOpen(false);
      toast({ title: 'Shift Details Saved', description: 'Saved to facility records and available for analytics.' });
    } catch (err: any) {
      toast({ variant: 'destructive', title: 'Error', description: err?.message || 'Failed to save details.' });
    }
  };

  // --- Demo data for morning shift ---
  const buildDemoData = () => {
    const today = new Date();
    const makeTime = (h: number, m: number) => {
      const d = new Date(today); d.setHours(h, m, 0, 0); return d.toISOString();
    };

    const demoServiceLogs = [
      { id: 'demo-svc-2', service_type: 'metro', quantity: 2, created_at: makeTime(9, 0), notes: 'Transit cards issued for job interviews', client_name: 'Marcus Williams' },
      { id: 'demo-svc-3', service_type: 'laundry', quantity: 1, created_at: makeTime(9, 45), notes: null, client_name: 'Robert Lee' },
      { id: 'demo-svc-6', service_type: 'metro', quantity: 1, created_at: makeTime(13, 15), notes: null, client_name: 'David Martinez' },
      { id: 'demo-svc-7', service_type: 'laundry', quantity: 2, created_at: makeTime(14, 30), notes: 'Afternoon laundry batch', client_name: 'James Taylor' },
    ];

    const demoActivityLogs = [
      // create_note entries excluded — notes already render from shiftNotes
      { id: 'demo-act-2', action_type: 'client_status_change', created_at: makeTime(9, 10), action_details: { client_name: 'James Taylor', from: 'checked-in', to: 'unbedded', staff_name: 'Sarah Johnson', property_location: 'Storage Room B, Bin 14' } },
      { id: 'demo-act-3', action_type: 'create_client', created_at: makeTime(10, 0), action_details: { client_name: 'William Harris', room_number: '12a' } },
      { id: 'demo-act-4', action_type: 'room_assignment', created_at: makeTime(10, 5), action_details: { client_name: 'William Harris', room_number: '12a' } },
      { id: 'demo-act-5', action_type: 'update_client', created_at: makeTime(11, 20), action_details: { client_name: 'Robert Lee', from_room: '104', to_room: '108', changed_fields: ['room_id'] } },
      { id: 'demo-act-6', action_type: 'submit_inspection', created_at: makeTime(12, 30), action_details: { template_name: 'Daily Building Walkthrough', area: 'Floor 2' } },
      // create_note entry excluded — notes already render from shiftNotes
      { id: 'demo-act-8', action_type: 'assign_pass', created_at: makeTime(14, 15), action_details: { client_name: 'David Martinez', pass_end: makeTime(15, 0) } },
      { id: 'demo-act-9', action_type: 'property_return', created_at: makeTime(15, 20), action_details: { client_name: 'James Taylor', property_location: 'Storage Room B, Bin 14' } },
    ];

    const demoAlerts = [
      { id: 'demo-alert-1', title: 'Supply Restock Needed', description: 'Toiletry supplies running low in storage room B.', type: 'warning', created_at: makeTime(9, 30) },
      { id: 'demo-alert-2', title: 'HVAC Issue Reported', description: 'Room 106 AC not functioning. Maintenance notified.', type: 'info', created_at: makeTime(11, 0) },
    ];

    const demoShiftNotes = [
      { id: 'demo-note-1', title: 'Shift Handoff Note (Incoming)', content: 'All clients accounted for. No incidents overnight. 2 new intakes expected this afternoon.', created_at: makeTime(8, 5), note_type: 'note' },
      { id: 'demo-note-2', title: 'Client Observation', content: 'Marcus Williams appeared distressed during breakfast. Recommended follow-up with case manager.', created_at: makeTime(8, 45), note_type: 'observation' },
      { id: 'demo-note-3', title: 'Minor Incident', content: 'Verbal altercation between two clients in common area. De-escalated by staff. No further action needed.', created_at: makeTime(14, 15), note_type: 'incident' },
      { id: 'demo-note-4', title: 'End of Shift Summary', content: 'All tasks completed. 1 new intake processed (William Harris). Supply restock request submitted. HVAC issue pending maintenance.', created_at: makeTime(15, 50), note_type: 'note' },
      { id: 'demo-note-5', title: 'Shift Handoff to Lisa Brown', content: 'Handing off to evening shift staff Lisa Brown and Tom Anderson. Key items: Marcus Williams needs case manager follow-up, Room 106 AC still down, new client William Harris in Room 112. All 10 bedded clients accounted for.', created_at: makeTime(15, 55), note_type: 'note' },
    ];

    return { demoServiceLogs, demoActivityLogs, demoAlerts, demoShiftNotes };
  };

  const isCurrentMorningShift = selectedShift.id === 'morning' && 
    format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const demoData = isDemoMode && isCurrentMorningShift ? buildDemoData() : null;

  // Fetch service logs for shift window
  const { data: serviceLogs = [] } = useQuery({
    queryKey: ['shift-services', startISO, endISO, isDemoMode],
    queryFn: async () => {
      if (demoData) return demoData.demoServiceLogs;
      const { data, error } = await supabase
        .from('service_logs')
        .select('*, clients:client_id(first_name, last_name)')
        .is('deleted_at', null)
        .gte('created_at', startISO)
        .lt('created_at', endISO)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data || []).map((log: any) => ({
        ...log,
        client_name: log.clients
          ? `${log.clients.first_name ?? ''} ${log.clients.last_name ?? ''}`.trim()
          : null,
        created_by: log.provided_by,
      }));
    },
  });

  // Fetch activity logs for shift window
  const { data: activityLogs = [] } = useQuery({
    queryKey: ['shift-activity', startISO, endISO, isDemoMode],
    queryFn: async () => {
      if (demoData) return demoData.demoActivityLogs;
      const { data, error } = await supabase
        .from('user_activity_logs')
        .select('*')
        .gte('created_at', startISO)
        .lt('created_at', endISO)
        .in('action_type', ['client_status_change', 'create_client', 'update_client', 'room_assignment', 'assign_pass', 'clear_pass', 'archive_client', 'submit_inspection', 'property_return', 'delete_service'])
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch alerts for shift window
  const { data: shiftAlerts = [] } = useQuery({
    queryKey: ['shift-alerts', startISO, endISO, isDemoMode],
    queryFn: async () => {
      if (demoData) return demoData.demoAlerts;
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .gte('created_at', startISO)
        .lt('created_at', endISO)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch shift notes (manual entries stored as notes with category 'shift-report')
  const { data: shiftNotes = [] } = useQuery({
    queryKey: ['shift-notes', startISO, endISO, isDemoMode],
    queryFn: async () => {
      if (demoData) return demoData.demoShiftNotes;
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .gte('created_at', startISO)
        .lt('created_at', endISO)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch bed counts for this shift window so the timeline can show start/end summaries only.
  const { data: bedCounts = [] } = useQuery({
    queryKey: ['shift-bed-counts', startISO, endISO, isDemoMode],
    enabled: !isDemoMode,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bed_counts')
        .select('*, bed_count_entries(*)')
        .or(`and(started_at.gte.${startISO},started_at.lt.${endISO}),and(signed_off_at.gte.${startISO},signed_off_at.lt.${endISO})`)
        .order('started_at', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Collect all actor UUIDs and resolve to full names for "by (Name)" attribution
  const actorIds = React.useMemo(() => {
    const ids = new Set<string>();
    (serviceLogs as any[]).forEach((s) => { if (s.created_by) ids.add(s.created_by); });
    (shiftAlerts as any[]).forEach((a) => { if (a.created_by) ids.add(a.created_by); });
    (shiftNotes as any[]).forEach((n) => { if (n.created_by) ids.add(n.created_by); });
    (activityLogs as any[]).forEach((l) => { if (l.user_id) ids.add(l.user_id); });
    (bedCounts as any[]).forEach((count) => {
      if (count.started_by) ids.add(count.started_by);
      if (count.signed_off_by) ids.add(count.signed_off_by);
      (count.bed_count_entries || []).forEach((entry: any) => { if (entry.marked_by) ids.add(entry.marked_by); });
    });
    return Array.from(ids);
  }, [serviceLogs, shiftAlerts, shiftNotes, activityLogs, bedCounts]);

  const noteClientIds = React.useMemo(() => {
    const ids = new Set<string>();
    (shiftNotes as any[]).forEach((n) => { if (n.client_id) ids.add(n.client_id); });
    return Array.from(ids);
  }, [shiftNotes]);

  const { data: noteClientNames = {} } = useQuery({
    queryKey: ['shift-note-clients', noteClientIds.sort().join(',')],
    enabled: noteClientIds.length > 0 && !isDemoMode,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, first_name, last_name')
        .in('id', noteClientIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data || []).forEach((c: any) => {
        map[c.id] = `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown';
      });
      return map;
    },
  });

  const { data: actorProfiles = {} } = useQuery({
    queryKey: ['shift-actor-profiles', actorIds.sort().join(',')],
    enabled: actorIds.length > 0 && !isDemoMode,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', actorIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data || []).forEach((p: any) => {
        if (p.id) map[p.id] = p.full_name || 'Unknown';
      });
      return map;
    },
  });

  const resolveActor = (id?: string | null): string | undefined => {
    if (!id) return undefined;
    return (actorProfiles as Record<string, string>)[id];
  };

  // Add entry mutation
  const addEntry = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const customTimestamp = new Date(`${entryDate}T${entryTime}:00`).toISOString();

      if (entryType === 'alert') {
        const { error } = await supabase.from('alerts').insert({
          title: entryTitle,
          description: entryDescription,
          type: 'info',
          status: 'active',
          created_by: user.id,
          created_at: customTimestamp,
        });
        if (error) throw error;
      } else {
        // Map UI value 'note' to DB-allowed 'general'
        const dbNoteType = entryType === 'note' ? 'general' : entryType;
        // Store as a note with shift-report category
        const { error } = await supabase.from('notes').insert({
          title: entryTitle,
          content: entryDescription,
          category: 'shift-report',
          note_type: dbNoteType,
          created_by: user.id,
          created_at: customTimestamp,
        });
        if (error) throw error;

        await logActivity({
          actionType: 'create_note',
          actionDetails: {
            note_title: entryTitle,
            note_source: 'shift_report',
            note_type: dbNoteType,
            timestamp: customTimestamp
          }
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['shift-notes'] });
      queryClient.invalidateQueries({ queryKey: ['shift-activity'] });
      setAddEntryOpen(false);
      setEntryTitle('');
      setEntryDescription('');
      setEntryType('note');
      setEntryDate(format(new Date(), 'yyyy-MM-dd'));
      setEntryTime(format(new Date(), 'HH:mm'));
      toast({ title: 'Entry Added', description: 'Shift report entry saved successfully.' });
    },
    onError: (err: any) => {
      toast({ variant: 'destructive', title: 'Error', description: err.message || 'Failed to add entry.' });
    },
  });

  // Edit entry mutation
  const editEntry = useMutation({
    mutationFn: async ({ event, title, description }: { event: TimelineEvent; title: string; description: string }) => {
      if (!event.sourceId || !event.sourceType) throw new Error('Cannot edit this entry');
      if (event.sourceType === 'note') {
        const { error } = await supabase.from('notes').update({ title, content: description }).eq('id', event.sourceId);
        if (error) throw error;
      } else if (event.sourceType === 'alert') {
        const { error } = await supabase.from('alerts').update({ title, description }).eq('id', event.sourceId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-notes'] });
      queryClient.invalidateQueries({ queryKey: ['shift-alerts'] });
      setEditingEvent(null);
      setSelectedEvent(null);
      toast({ title: 'Entry Updated', description: 'Shift report entry updated successfully.' });
    },
    onError: (err: any) => {
      toast({ variant: 'destructive', title: 'Error', description: err.message || 'Failed to update entry.' });
    },
  });

  // Summarize services
  const normalize = (t: string) => {
    const lower = t.toLowerCase();
    if (lower.includes('metro')) return 'metro';
    if (lower.includes('laundry')) return 'laundry';
    if (lower.includes('meal') || lower.includes('dinner') || lower.includes('lunch') || lower.includes('breakfast')) return 'meal';
    if (lower.includes('juice') || lower.includes('drink')) return 'juice';
    return lower;
  };

  const serviceCounts = serviceLogs.reduce(
    (acc, log) => {
      const t = (log.service_type || '').toLowerCase();
      const key = normalize(log.service_type);
      if (key in acc) acc[key as keyof typeof acc] += (log.quantity || 1);
      if (t === 'breakfast') acc.breakfast += (log.quantity || 1);
      else if (t === 'lunch') acc.lunch += (log.quantity || 1);
      else if (t === 'dinner') acc.dinner += (log.quantity || 1);
      else if (t === 'milk') acc.milk += (log.quantity || 1);
      else if (t === 'juice') acc.juice += (log.quantity || 1);
      return acc;
    },
    { metro: 0, laundry: 0, meal: 0, juice: 0, breakfast: 0, lunch: 0, dinner: 0, milk: 0 } as Record<string, number>
  );

  // Fetch meal deliveries for the shift's date(s)
  const mealDateStart = format(bounds.start, 'yyyy-MM-dd');
  const mealDateEnd = format(bounds.end, 'yyyy-MM-dd');
  const { data: mealDeliveries = [] } = useQuery({
    queryKey: ['shift-meal-deliveries', mealDateStart, mealDateEnd, isDemoMode],
    enabled: !isDemoMode,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('meal_deliveries')
        .select('*')
        .gte('service_date', mealDateStart)
        .lte('service_date', mealDateEnd);
      if (error) throw error;
      return (data || []) as Array<{ meal_type: 'breakfast'|'lunch'|'dinner'|'milk'|'juice'; quantity_received: number; menu_description: string | null }>;
    },
  });
  const mealTotals = mealDeliveries.reduce(
    (acc, d) => {
      acc[d.meal_type].received += d.quantity_received || 0;
      if (d.menu_description) acc[d.meal_type].menus.push(d.menu_description);
      return acc;
    },
    {
      breakfast: { received: 0, menus: [] as string[] },
      lunch: { received: 0, menus: [] as string[] },
      dinner: { received: 0, menus: [] as string[] },
      milk: { received: 0, menus: [] as string[] },
      juice: { received: 0, menus: [] as string[] },
    }
  );

  // Hospital tracking removed — no PHI captured
  const hospitalCount = 0;

  // Facility snapshot — current clients & rooms for accountability auto-populate
  const { data: facilitySnapshot } = useQuery({
    queryKey: ['shift-facility-snapshot', reportSiteId, isDemoMode],
    enabled: !!reportSiteId && !isDemoMode,
    queryFn: async () => {
      const [clientsRes, roomsRes] = await Promise.all([
        supabase
          .from('clients')
          .select('id, status, room_id, is_returnee, pass_type, pass_end')
          .eq('site_id', reportSiteId!)
          .is('archived_at', null),
        supabase
          .from('rooms')
          .select('capacity')
          .eq('site_id', reportSiteId!),
      ]);
      return {
        clients: (clientsRes.data ?? []) as any[],
        rooms: (roomsRes.data ?? []) as any[],
      };
    },
  });

  const snap = facilitySnapshot ?? { clients: [], rooms: [] };
  const checkedInClients = snap.clients.filter((c) => c.status === 'checked-in');
  const totalCapacity = snap.rooms.reduce((sum, r) => sum + (r.capacity || 0), 0);
  const assignedBeds = snap.clients.filter((c) => c.room_id).length;
  const reservedBedsCount = snap.clients.filter((c) => c.status === 'reserved').length;
  const hospitalCurrent = 0; // hospital status removed
  const unbeddedCount = snap.clients.filter((c) => c.status === 'unbedded').length;
  const notPresentCount = snap.clients.filter((c) => c.status === 'not_present').length;

  // Pass categorization — exact-match on canonical pass_type values, with legacy fallback
  // by pass_end distance from now for rows where pass_type is null but pass is active.
  const nowMs = Date.now();
  const onPassNow = snap.clients.filter((c) => c.pass_end && new Date(c.pass_end).getTime() > nowMs);
  const latePassesCount = onPassNow.filter((c) => {
    if (c.pass_type === 'late') return true;
    if (!c.pass_type) {
      const hours = (new Date(c.pass_end).getTime() - nowMs) / 36e5;
      return hours <= 6;
    }
    return false;
  }).length;
  const overnightPassesCount = onPassNow.filter((c) => {
    if (c.pass_type === 'overnight') return true;
    if (!c.pass_type) {
      const hours = (new Date(c.pass_end).getTime() - nowMs) / 36e5;
      return hours > 6 && hours <= 36;
    }
    return false;
  }).length;
  const multiUsePassesCount = onPassNow.filter((c) => {
    if (c.pass_type === 'multi_use') return true;
    if (!c.pass_type) {
      const hours = (new Date(c.pass_end).getTime() - nowMs) / 36e5;
      return hours > 36;
    }
    return false;
  }).length;
  const sleepingTonight = Math.max(0, checkedInClients.length - onPassNow.length);

  // Check-in events that occurred during this shift window
  const shiftCheckIns = React.useMemo(() => {
    const events: { clientId: string; checkInTime: string }[] = [];
    (activityLogs as any[]).forEach((log) => {
      const d = (log.action_details || {}) as Record<string, any>;
      const clientId = d.client_id;
      if (!clientId) return;
      if (log.action_type === 'create_client') {
        events.push({ clientId, checkInTime: log.created_at });
      } else if (log.action_type === 'client_status_change' && (d.to ?? d.new_status) === 'checked-in') {
        events.push({ clientId, checkInTime: log.created_at });
      }
    });
    return events;
  }, [activityLogs]);

  // Returnees this shift = clients checking in now after >3 days absent (Source A)
  // PLUS daily_waitlist entries during shift window with reason 'Returnee' (Source B), deduped.
  const { data: returneesData = { count: 0, names: [] as string[] } } = useQuery({
    queryKey: ['shift-returnees', startISO, endISO, reportSiteId, shiftCheckIns.map((e) => e.clientId).sort().join(',')],
    enabled: !isDemoMode,
    queryFn: async () => {
      const returnees = new Map<string, { name: string; daysAway?: number; at?: string }>();

      // ── Source A: check-ins this shift after >3 days departure ──
      if (shiftCheckIns.length > 0) {
        const clientIds = Array.from(new Set(shiftCheckIns.map((e) => e.clientId)));
        const { data: priorLogs } = await supabase
          .from('user_activity_logs')
          .select('action_type, action_details, created_at')
          .in('action_type', ['client_status_change', 'archive_client'])
          .lt('created_at', startISO)
          .order('created_at', { ascending: false })
          .limit(2000);
        const lastDeparture: Record<string, string> = {};
        (priorLogs || []).forEach((log: any) => {
          const d = log.action_details || {};
          const cid = d.client_id;
          if (!cid || !clientIds.includes(cid)) return;
          if (lastDeparture[cid]) return;
          const to = d.to ?? d.new_status;
          if (log.action_type === 'archive_client' || to === 'checked-out' || to === 'unbedded' || to === 'not_present') {
            lastDeparture[cid] = log.created_at;
          }
        });
        const qualifying: { clientId: string; daysAway: number; at: string }[] = [];
        shiftCheckIns.forEach(({ clientId, checkInTime }) => {
          const prev = lastDeparture[clientId];
          if (!prev) return;
          const days = Math.floor((new Date(checkInTime).getTime() - new Date(prev).getTime()) / 86400000);
          if (days > 3) qualifying.push({ clientId, daysAway: days, at: checkInTime });
        });
        if (qualifying.length > 0) {
          const { data: clientRows } = await supabase
            .from('clients')
            .select('id, first_name, last_name')
            .in('id', qualifying.map((q) => q.clientId));
          const nameById: Record<string, string> = {};
          (clientRows || []).forEach((c: any) => {
            nameById[c.id] = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || 'Client';
          });
          qualifying.forEach(({ clientId, daysAway, at }) => {
            returnees.set(clientId, { name: nameById[clientId] || 'Client', daysAway, at });
          });
        }
      }

      // ── Source B: daily_waitlist with reason 'Returnee' in shift window ──
      let waitlistQuery = supabase
        .from('daily_waitlist')
        .select('client_id, arrival_name, reason, arrival_time')
        .ilike('reason', 'returnee')
        .gte('arrival_time', startISO)
        .lt('arrival_time', endISO);
      if (reportSiteId) waitlistQuery = waitlistQuery.eq('site_id', reportSiteId);
      const { data: waitlistRows } = await waitlistQuery;
      (waitlistRows || []).forEach((row: any) => {
        const key = row.client_id || `name:${(row.arrival_name || '').toLowerCase().trim()}`;
        if (!key || returnees.has(key)) return;
        returnees.set(key, { name: row.arrival_name || 'Walk-in', at: row.arrival_time });
      });

      const fmtTime = (iso?: string) => {
        if (!iso) return '';
        try {
          return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        } catch { return ''; }
      };
      const names = Array.from(returnees.values()).map((v) => {
        const t = fmtTime(v.at);
        const suffix = [v.daysAway != null ? `${v.daysAway}d` : null, t || null].filter(Boolean).join(' • ');
        return suffix ? `${v.name} (${suffix})` : v.name;
      });
      return { count: returnees.size, names };
    },
  });
  const returneesCount = returneesData.count;
  const returneesNames = returneesData.names;


  // Intakes during shift window (new client created)
  const intakesCount = (activityLogs as any[]).filter((log) => log.action_type === 'create_client').length;

  const serviceTotalFieldIds = new Set([
    'metrocards', 'laundryCards', 'mealsDistributed', 'juiceDistributed', 'hospital',
    'clientCount', 'bedsAvailable', 'reservedBeds', 'returnees',
    'latePasses', 'overnightPasses', 'multiUsePasses', 'intakes', 'unbedded',
    'breakfastReceived', 'breakfastDistributed', 'breakfastDiscarded', 'breakfastMenu',
    'lunchReceived', 'lunchDistributed', 'lunchDiscarded', 'lunchMenu',
    'dinnerReceived', 'dinnerDistributed', 'dinnerDiscarded', 'dinnerMenu',
    'milkReceived', 'milkDistributed', 'milkDiscarded', 'milkBrand',
    'juiceReceived', 'juiceDistributed', 'juiceDiscarded', 'juiceBrand',
  ]);
  const serviceTotalDetails: ShiftDetails = {
    metrocards: String(serviceCounts.metro),
    laundryCards: String(serviceCounts.laundry),
    mealsDistributed: String(serviceCounts.meal),
    hospital: String(hospitalCount),
    clientCount: String(checkedInClients.length),
    bedsAvailable: String(Math.max(0, totalCapacity - assignedBeds)),
    reservedBeds: String(reservedBedsCount),
    returnees: returneesNames.length > 0
      ? `${returneesCount} — ${returneesNames.join(', ')}`
      : String(returneesCount),
    latePasses: String(latePassesCount),
    overnightPasses: String(overnightPassesCount),
    multiUsePasses: String(multiUsePassesCount),
    intakes: String(intakesCount),
    unbedded: String(unbeddedCount),
    breakfastReceived: String(mealTotals.breakfast.received),
    breakfastDistributed: String(serviceCounts.breakfast),
    breakfastDiscarded: String(Math.max(0, mealTotals.breakfast.received - serviceCounts.breakfast)),
    breakfastMenu: mealTotals.breakfast.menus.join('; '),
    lunchReceived: String(mealTotals.lunch.received),
    lunchDistributed: String(serviceCounts.lunch),
    lunchDiscarded: String(Math.max(0, mealTotals.lunch.received - serviceCounts.lunch)),
    lunchMenu: mealTotals.lunch.menus.join('; '),
    dinnerReceived: String(mealTotals.dinner.received),
    dinnerDistributed: String(serviceCounts.dinner),
    dinnerDiscarded: String(Math.max(0, mealTotals.dinner.received - serviceCounts.dinner)),
    dinnerMenu: mealTotals.dinner.menus.join('; '),
    milkReceived: String(mealTotals.milk.received),
    milkDistributed: String(serviceCounts.milk),
    milkDiscarded: String(Math.max(0, mealTotals.milk.received - serviceCounts.milk)),
    milkBrand: mealTotals.milk.menus.join('; '),
    juiceReceived: String(mealTotals.juice.received),
    juiceDistributed: String(serviceCounts.juice),
    juiceDiscarded: String(Math.max(0, mealTotals.juice.received - serviceCounts.juice)),
    juiceBrand: mealTotals.juice.menus.join('; '),
  };
  const reportDetails = { ...shiftDetails, ...serviceTotalDetails };

  // Extract staff present during shift from login events
  const staffPresent = activityLogs
    .filter((log) => log.action_type === 'login')
    .map((log) => {
      const details = log.action_details as Record<string, any> | null;
      const name = details?.user || 'Unknown';
      const role = details?.role || '';
      return { name, role };
    });

  const fieldValue = (fieldId: string) => reportDetails[fieldId] || '';
  const completedDetailCount = enabledReportSections.reduce(
    (count, section) => count + section.fields.filter((field) => fieldValue(field.id).trim()).length,
    0
  );
  const printableValue = (value: string) => (value && value.trim() ? escapeHtml(value) : '—');
  const renderFieldRow = (field: { id: string; label: string }) => {
    const raw = fieldValue(field.id);
    if (field.id === 'returnees' && raw.includes(' — ')) {
      const [count, ...rest] = raw.split(' — ');
      const names = rest.join(' — ');
      return `<tr><td class="lbl">${escapeHtml(field.label)}:</td><td colspan="3"><strong>${escapeHtml(count)}</strong> — ${escapeHtml(names)}</td></tr>`;
    }
    return `<tr><td class="lbl">${escapeHtml(field.label)}:</td><td>${printableValue(raw)}</td></tr>`;
  };
  const renderStatCell = (field: { id: string; label: string }) => {
    const raw = fieldValue(field.id);
    if (field.id === 'returnees' && raw.includes(' — ')) {
      const [count, ...rest] = raw.split(' — ');
      const names = rest.join(' — ');
      return `<div class="stat-cell span-2"><div class="stat-label">${escapeHtml(field.label)}</div><div class="stat-value">${escapeHtml(count)}</div><div class="stat-sub">${escapeHtml(names)}</div></div>`;
    }
    return `<div class="stat-cell"><div class="stat-label">${escapeHtml(field.label)}</div><div class="stat-value">${printableValue(raw)}</div></div>`;
  };
  const buildReportSectionsHtml = () => enabledReportSections.map((section) => {
    if (section.id === 'accountability' || section.id === 'equipment') {
      return `
        <h3 class="grid-section-title">${escapeHtml(section.title)}</h3>
        <div class="stat-grid">
          ${section.fields.map(renderStatCell).join('')}
        </div>`;
    }
    return `
      <table class="acc-table">
        <thead><tr><th colspan="2">${escapeHtml(section.title)}</th></tr></thead>
        <tbody>
          ${section.fields.map(renderFieldRow).join('')}
        </tbody>
      </table>`;
  }).join('');

  // Print handler
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast({ variant: 'destructive', title: 'Error', description: 'Please allow popups to print' }); return; }


    const staffList = staffPresent.length > 0
      ? staffPresent.map(s => `${escapeHtml(s.name)}${s.role ? ` (${escapeHtml(s.role)})` : ''}`).join(', ')
      : 'No staff logins recorded';

    const pageSize = 'letter portrait';

    const headerTable = `
      <table class="info-table">
        <tr>
          <th style="width:15%">Date:</th><td style="width:30%">${escapeHtml(format(selectedDate, 'M/d/yyyy'))}</td>
          <th style="width:15%">Shift:</th><td style="width:40%">${escapeHtml(shiftLabel)}</td>
        </tr>
      </table>`;

    const reportSectionsHtml = buildReportSectionsHtml();

    printWindow.document.write(`<html><head><title>Shift Report</title>
      <style>
        @page { size: ${pageSize}; margin: 0.75in; }
        body { font-family: Arial, sans-serif; margin: 20px; color: #000; }
        .print-header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 12px; margin-bottom: 16px; }
        .print-header h1 { font-size: 20pt; margin: 0 0 4px; }
        .print-header h2 { font-size: 14pt; margin: 0 0 4px; font-weight: normal; }
        .print-header .staff-list { font-size: 10pt; color: #444; margin-top: 8px; }
        .info-table, .acc-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 10pt; }
        .info-table th, .info-table td, .acc-table th, .acc-table td { border: 1px solid #999; padding: 5px 8px; text-align: left; vertical-align: top; }
        .info-table th, .acc-table thead th { background: #f0f0f0; font-weight: 700; }
        .acc-table td.lbl { background: #fafafa; font-weight: 600; width: 20%; }
        .acc-table td:not(.lbl) { width: 15%; }
        .summary-row { display: flex; gap: 12px; margin-bottom: 14px; }
        .summary-box { border: 1px solid #ccc; padding: 6px 12px; text-align: center; flex: 1; }
        .summary-box .count { font-size: 16pt; font-weight: bold; }
        .summary-box .label { font-size: 9pt; color: #666; }
        .timeline-item { display: flex; gap: 12px; padding: 7px 0; border-bottom: 1px solid #eee; }
        .timeline-item .time { min-width: 70px; font-size: 9pt; color: #666; }
        .timeline-item .title { font-weight: 600; font-size: 10.5pt; }
        .timeline-item .desc { font-size: 9pt; color: #444; white-space: pre-wrap; }
        .timeline-item .badge { font-size: 8pt; background: #eee; padding: 1px 6px; border-radius: 3px; margin-left: 4px; }
        .section-title { font-size: 12pt; font-weight: bold; margin: 14px 0 6px; border-bottom: 1px solid #ccc; padding-bottom: 3px; }
        .grid-section-title { font-size: 11pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; margin: 14px 0 6px; border-bottom: 1px solid #999; padding-bottom: 3px; }
        .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: 14px; }
        .stat-cell { border: 1px solid #bbb; padding: 6px 8px; min-height: 44px; display: flex; flex-direction: column; justify-content: space-between; }
        .stat-cell .stat-label { font-size: 8.5pt; color: #555; text-transform: uppercase; letter-spacing: 0.03em; }
        .stat-cell .stat-value { font-size: 14pt; font-weight: 700; line-height: 1.1; }
        .stat-cell.span-2 { grid-column: span 2; }
        .stat-cell .stat-sub { font-size: 9pt; color: #555; font-weight: 400; }
        .timeline-title { font-size: 16pt; font-weight: 700; margin: 40px 0 10px; border-bottom: 2px solid #000; padding-bottom: 4px; page-break-after: avoid; }
        .footer { margin-top: 20px; text-align: center; font-size: 8pt; color: #999; border-top: 1px solid #ccc; padding-top: 6px; }
        @media print { button { display: none; } }
      </style></head><body>` +
      `<div class="print-header">
        <h1>${escapeHtml(organizationName)} Shift Report</h1>
      </div>
      ${headerTable}
      <div class="summary-row">
        <div class="summary-box"><div class="count">${checkedInClients.length}</div><div class="label">Checked In</div></div>
        <div class="summary-box"><div class="count">${notPresentCount}</div><div class="label">Not Present</div></div>
        <div class="summary-box"><div class="count">${onPassNow.length}</div><div class="label">On Pass</div></div>
        <div class="summary-box"><div class="count">${unbeddedCount}</div><div class="label">Unbedded</div></div>
      </div>
      ${reportSectionsHtml}
      <div class="timeline-title">Shift Timeline</div>
      ${dedupedTimeline.map(e => `<div class="timeline-item"><div class="time">${format(new Date(e.time), 'h:mm a')}</div><div><span class="title">${escapeHtml(e.title)}</span><span class="badge">${e.type}</span>${e.description ? `<div class="desc">${escapeHtml(formatNoteText(e.description))}</div>` : ''}</div></div>`).join('')}
      <div class="footer">Confidential — For Internal Use Only — Generated ${format(new Date(), 'MMMM d, yyyy h:mm a')}</div>
    </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  // Download as PDF
  const handleDownload = async () => {
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 40;
      let y = 40;

      const staffList = staffPresent.length > 0
        ? staffPresent.map(s => `${s.name}${s.role ? ` (${s.role})` : ''}`).join(', ')
        : 'No staff logins recorded';

      const contentW = pageWidth - margin * 2;
      const vv = (value: string) => (value && value.trim() ? value : '—');

      // Title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(`${organizationName} Shift Report`, pageWidth / 2, y, { align: 'center' });
      y += 22;
      doc.setDrawColor(0);
      doc.setLineWidth(1);
      doc.line(margin, y, pageWidth - margin, y);
      y += 12;
      doc.setLineWidth(0.5);

      // --- Header info table (Date, Shift, Supervisor, RAs, Guards, Call-Outs) ---
      const drawRow = (cells: { label: string; value: string; labelW: number; valueW: number }[], rowH = 18) => {
        let x = margin;
        doc.setFontSize(9);
        for (const c of cells) {
          // label cell
          doc.setFillColor(240, 240, 240);
          doc.rect(x, y, c.labelW, rowH, 'FD');
          doc.setFont('helvetica', 'bold');
          doc.text(c.label, x + 4, y + 12);
          x += c.labelW;
          // value cell
          doc.setFillColor(255, 255, 255);
          doc.rect(x, y, c.valueW, rowH, 'FD');
          doc.setFont('helvetica', 'normal');
          const txt = doc.splitTextToSize(vv(c.value), c.valueW - 8);
          doc.text(txt, x + 4, y + 12);
          x += c.valueW;
        }
        y += rowH;
      };

      const lW = contentW * 0.18;
      const vW = contentW * 0.32;
      drawRow([
        { label: 'Date:', value: format(selectedDate, 'M/d/yyyy'), labelW: lW, valueW: vW },
        { label: 'Shift:', value: shiftLabel, labelW: lW, valueW: vW },
      ]);
      y += 8;

      const drawSection = (sectionTitle: string, rows: { label: string; value: string }[]) => {
        if (y > doc.internal.pageSize.getHeight() - 90) { doc.addPage(); y = 40; }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(230, 230, 230);
        doc.rect(margin, y, contentW, 18, 'FD');
        doc.text(sectionTitle, margin + 4, y + 12);
        y += 18;

        rows.forEach((row) => {
          const rowH = 18;
          if (y + rowH > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); y = 40; }
          doc.setFontSize(9);
          doc.setFillColor(250, 250, 250);
          doc.rect(margin, y, contentW * 0.32, rowH, 'FD');
          doc.setFont('helvetica', 'bold');
          doc.text(`${row.label}:`, margin + 4, y + 12, { maxWidth: contentW * 0.32 - 8 });
          doc.setFillColor(255, 255, 255);
          doc.rect(margin + contentW * 0.32, y, contentW * 0.68, rowH, 'FD');
          doc.setFont('helvetica', 'normal');
          doc.text(doc.splitTextToSize(vv(row.value), contentW * 0.68 - 8), margin + contentW * 0.32 + 4, y + 12);
          y += rowH;
        });
        y += 8;
      };

      enabledReportSections.forEach((section) => {
        drawSection(section.title, section.fields.map((field) => ({ label: field.label, value: fieldValue(field.id) })));
      });

      // Timeline
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Shift Timeline', margin, y);
      y += 14;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      dedupedTimeline.forEach(e => {
        if (y > doc.internal.pageSize.getHeight() - 60) {
          doc.addPage();
          y = 40;
        }
        const time = format(new Date(e.time), 'h:mm a');
        doc.setFont('helvetica', 'bold');
        doc.text(time, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.text(`${e.title} [${e.type}]`, margin + 65, y);
        if (e.description) {
          y += 11;
          doc.setFontSize(8);
          doc.setTextColor(100);
          const lines = doc.splitTextToSize(formatNoteText(e.description), pageWidth - margin * 2 - 65);
          doc.text(lines, margin + 65, y);
          y += lines.length * 10;
          doc.setTextColor(0);
          doc.setFontSize(9);
        } else {
          y += 14;
        }
      });

      // Footer
      y += 10;
      doc.setDrawColor(180);
      doc.line(margin, y, pageWidth - margin, y);
      y += 12;
      doc.setFontSize(7);
      doc.setTextColor(150);
      doc.text(`Confidential — For Internal Use Only — Generated ${format(new Date(), 'MMMM d, yyyy h:mm a')}`, pageWidth / 2, y, { align: 'center' });

      doc.save(`Shift_Report_${format(selectedDate, 'yyyy-MM-dd')}_${shift}.pdf`);
      toast({ title: 'Downloaded', description: 'Shift report PDF saved.' });
    } catch (err) {
      console.error('Download error:', err);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to download report.' });
    }
  };

  // Build timeline events
  const serviceIcon = (type: string) => {
    const n = normalize(type);
    if (n === 'metro') return <Train className="h-4 w-4 text-primary" />;
    if (n === 'laundry') return <Shirt className="h-4 w-4 text-accent-foreground" />;
    if (n === 'meal') return <UtensilsCrossed className="h-4 w-4 text-warning" />;
    if (n === 'juice') return <CupSoda className="h-4 w-4 text-success" />;
    return <Activity className="h-4 w-4 text-muted-foreground" />;
  };

  // Title labels for activity actions
  const ACTIVITY_TITLES: Record<string, string> = {
    create_client: 'New Intake',
    client_status_change: 'Status Change',
    room_assignment: 'Bed Assigned',
    update_client: 'Bed Change',
    assign_pass: 'Pass Issued',
    clear_pass: 'Pass Cleared',
    create_note: 'Note Added',
    submit_inspection: 'Inspection Completed',
    property_return: 'Belongings Returned',
    archive_client: 'Archived',
    bed_count: 'Bed Count',
    delete_service: 'Service Removed',
  };

  // Friendly label for service types
  const serviceLabel = (type: string) => {
    const n = normalize(type);
    if (n === 'metro') return 'Metro Pass';
    if (n === 'laundry') return 'Laundry';
    if (n === 'meal') return 'Meal';
    if (n === 'juice') return 'Juice';
    return type;
  };

  // Trivial vs substantive update_client diff keys
  const TRIVIAL_UPDATE_KEYS = new Set([
    'phone', 'photo', 'photo_url', 'check_in_time', 'check_out_time',
    'last_activity', 'updated_at', 'email', 'address',
  ]);
  const SUBSTANTIVE_UPDATE_KEYS = new Set([
    'room_id', 'room', 'room_number', 'case_worker_id', 'case_worker',
    'status', 'pass_start', 'pass_end', 'first_name', 'last_name',
  ]);

  // Only operational service entries appear in the shift timeline.
  // Food-related services (meal/milk/juice/breakfast/lunch/dinner) are routine
  // and excluded — they're tracked in the daily service totals, not the shift log.
  const SHIFT_SERVICE_TYPES = new Set(['metro', 'laundry']);

  const bedCountTimelineEvents: TimelineEvent[] = (bedCounts as any[]).flatMap((count) => {
    const entries = (count.bed_count_entries || []) as any[];
    const totals = entries.reduce(
      (acc, entry) => {
        if (entry.result === 'present') acc.present += 1;
        else if (entry.result === 'not_present') acc.notPresent += 1;
        else if (entry.result === 'pass_acknowledged') acc.pass += 1;
        else if (entry.result === 'unbedded') acc.unbedded += 1;
        return acc;
      },
      { present: 0, notPresent: 0, pass: 0, unbedded: 0 }
    );
    const completedBy = Array.from(new Set(entries.map((entry) => resolveActor(entry.marked_by)).filter(Boolean) as string[]));
    const signedOffBy = resolveActor(count.signed_off_by);
    const startedBy = resolveActor(count.started_by);
    const events: TimelineEvent[] = [];

    if (count.started_at && count.started_at >= startISO && count.started_at < endISO) {
      events.push({
        id: `bed-count-start-${count.id}`,
        time: count.started_at,
        type: 'activity',
        category: 'bed-count',
        icon: <Bed className="h-4 w-4 text-primary" />,
        title: 'Bed Count Started',
        description: `${startedBy ? `Started by ${startedBy}` : 'Started'} — ${entries.length} clients`,
        badgeVariant: 'outline',
        createdBy: count.started_by || undefined,
        actorName: startedBy,
      });
    }

    if (count.signed_off_at && count.signed_off_at >= startISO && count.signed_off_at < endISO) {
      const otherMarkers = completedBy.filter((n) => n !== signedOffBy);
      const byLine = signedOffBy
        ? `Completed by ${signedOffBy}${otherMarkers.length > 0 ? ` (marked by ${otherMarkers.join(', ')})` : ''}`
        : completedBy.length > 0
          ? `Completed by ${completedBy.join(', ')}`
          : 'Completed';
      events.push({
        id: `bed-count-complete-${count.id}`,
        time: count.signed_off_at,
        type: 'activity',
        category: 'bed-count',
        icon: <Bed className="h-4 w-4 text-success" />,
        title: 'Bed Count Completed',
        description: `${byLine} — Present: ${totals.present} · Not Present: ${totals.notPresent} · Pass: ${totals.pass} · Unbedded: ${totals.unbedded}`,
        badgeVariant: 'outline',
        createdBy: count.signed_off_by || undefined,
        actorName: signedOffBy,
      });
    }

    return events;
  });

  const timeline: TimelineEvent[] = [
    // One line per service log (no double "Card provided" duplicate)
    ...serviceLogs
      .filter((log) => SHIFT_SERVICE_TYPES.has(normalize(log.service_type)))
      .map((log) => {
      const qty = log.quantity || 1;
      const label = serviceLabel(log.service_type);
      const clientName = (log as any).client_name || (log as any).clients?.first_name;
      const actorName = resolveActor((log as any).created_by);
      const desc = [
        clientName ? clientName : null,
        qty > 1 ? `Qty ${qty}` : null,
        log.notes || null,
        actorName ? `by ${actorName}` : null,
      ].filter(Boolean).join(' — ');
      const svcCategory = normalize(log.service_type);
      return {
        id: `svc-${log.id}`,
        time: log.created_at!,
        type: 'service' as const,
        category: svcCategory,
        icon: serviceIcon(log.service_type),
        title: label,
        description: desc || undefined,
        badgeVariant: 'secondary' as const,
        createdBy: (log as any).created_by || undefined,
        actorName,
        clientName: clientName || undefined,
      };
    }),
    ...shiftAlerts.map((a) => {
      const actorName = resolveActor((a as any).created_by);
      const baseDesc = a.description || '';
      const description = actorName
        ? (baseDesc ? `${baseDesc} — by ${actorName}` : `by ${actorName}`)
        : baseDesc;
      return {
        id: `alert-${a.id}`,
        time: a.created_at!,
        type: 'alert' as const,
        category: 'alert',
        icon: <AlertTriangle className="h-4 w-4 text-destructive" />,
        title: a.title,
        description,
        badgeVariant: (a.type === 'critical' ? 'destructive' : 'outline') as 'destructive' | 'outline',
        createdBy: (a as any).created_by || undefined,
        actorName,
        sourceId: a.id,
        sourceType: 'alert' as const,
      };
    }),
    ...shiftNotes.map((note) => {
      const hasClientMention = /@\[[^\]]+\]\(client:[^)]+\)/.test(note.content || '');
      const actorName = resolveActor((note as any).created_by);
      const clientName = (note as any).client_id ? (noteClientNames as Record<string, string>)[(note as any).client_id] : undefined;
      const cat = (note as any).category;
      let baseDesc = note.content || '';
      if ((cat === 'property' || cat === 'property_return') && baseDesc) {
        try {
          const p = JSON.parse(baseDesc);
          const lines: string[] = [];
          if (cat === 'property') {
            if (p.propertyLocation) lines.push(`Location: ${p.propertyLocation}`);
            if (p.electronics) lines.push(`Electronics: ${p.electronics}`);
            if (p.documents) lines.push(`Documents: ${p.documents}`);
            if (p.valuables) lines.push(`Valuables: ${p.valuables}`);
            if (p.additionalNotes) lines.push(`Notes: ${p.additionalNotes}`);
          } else {
            if (p.allReturned) {
              lines.push('All items returned');
            } else {
              if (Array.isArray(p.returnedItems) && p.returnedItems.length) lines.push(`Returned: ${p.returnedItems.join('; ')}`);
              if (Array.isArray(p.notReturned) && p.notReturned.length) lines.push(`Not returned: ${p.notReturned.join(', ')}`);
            }
            if (p.missingOrDamaged && p.missingOrDamaged !== 'None') lines.push(`Missing/Damaged: ${p.missingOrDamaged}`);
          }
          if (lines.length) baseDesc = lines.join('\n');
        } catch {
          // leave as-is
        }
      }
      const attribution = [
        clientName ? `for ${clientName}` : null,
        actorName ? `by ${actorName}` : null,
      ].filter(Boolean).join(' · ');
      const description = attribution
        ? (baseDesc ? `${baseDesc}\n\n${attribution}` : attribution)
        : (baseDesc || undefined);
      return {
        id: `note-${note.id}`,
        time: note.created_at!,
        type: 'manual' as const,
        category: 'note',
        icon: <FileText className="h-4 w-4 text-primary" />,
        title: note.title,
        description,
        badgeVariant: 'default' as const,
        createdBy: (note as any).created_by || undefined,
        actorName,
        sourceId: note.id,
        sourceType: 'note' as const,
        caseManagerNotified: hasClientMention,
      };
    }),
    ...bedCountTimelineEvents,
    ...activityLogs
      .map((log) => {
        const actionType = log.action_type;
        const details = (log.action_details || {}) as Record<string, any>;
        const clientName = details.client_name || details.client || details.name;
        const room = details.room_number || details.room || details.bed;
        const resolvedActor = resolveActor((log as any).user_id);
        const staffName = resolvedActor || details.staff_name || details.actor || details.author || details.by;

        if (actionType === 'bed_count') return null;
        // Skip property_return activity logs — already represented by the matching note entry to avoid duplicates
        if (actionType === 'property_return') return null;

        // Suppress profile edits from the shift timeline — they live in the client's Activity Log
        if (actionType === 'update_client') return null;

        // Skip routine status changes — only keep meaningful ones
        const isStatusChange = actionType === 'client_status_change';
        const toStatus = details.to ?? details.new_status;
        const fromStatus = details.from ?? details.previous_status ?? details.old_status;
        const isUnbedded = isStatusChange && toStatus === 'unbedded';
        const isHospital = false;
        const isReturnFromHospital = false;
        if (isStatusChange && !['unbedded', 'reserved', 'waitlist'].includes(toStatus)) {
          return null;
        }

        // Title
        let title = ACTIVITY_TITLES[actionType] || actionType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        if (isUnbedded) title = 'Unbedded';
        else if (isHospital) title = 'Sent to Hospital';
        else if (isReturnFromHospital) title = 'Returned from Hospital';

        // Icon
        let icon: React.ReactNode = <FileText className="h-4 w-4 text-muted-foreground" />;
        if (actionType === 'create_client') icon = <UserPlus className="h-4 w-4 text-success" />;
        else if (isUnbedded) icon = <DoorOpen className="h-4 w-4 text-warning" />;
        else if (isHospital || isReturnFromHospital) icon = <HeartPulse className="h-4 w-4 text-destructive" />;
        else if (isStatusChange) icon = <UserCheck className="h-4 w-4 text-primary" />;
        else if (actionType === 'room_assignment' || actionType === 'update_client') icon = <Bed className="h-4 w-4 text-accent-foreground" />;
        else if (actionType === 'assign_pass' || actionType === 'clear_pass') icon = <CalendarCheck2 className="h-4 w-4 text-warning" />;
        else if (actionType === 'create_note') icon = <FileText className="h-4 w-4 text-primary" />;
        else if (actionType === 'submit_inspection') icon = <UserCheck className="h-4 w-4 text-success" />;
        else if (actionType === 'property_return') icon = <Archive className="h-4 w-4 text-warning" />;
        else if (actionType === 'archive_client') icon = <Archive className="h-4 w-4 text-destructive" />;
        else if (actionType === 'delete_service') icon = <Activity className="h-4 w-4 text-muted-foreground" />;
        else if (actionType === 'bed_count') icon = <Bed className="h-4 w-4 text-primary" />;

        // Description — concise, name + bed + context only
        let desc: string | undefined;
        const bedLabel = room ? `Bed ${room}` : undefined;

        if (actionType === 'create_client' && clientName) {
          desc = bedLabel ? `${clientName} (${bedLabel})` : clientName;
        } else if (isUnbedded && clientName) {
          const loc = details.property_location || details.storage_location;
          desc = `${clientName}${staffName ? ` (by ${staffName})` : ''}${loc ? ` — Belongings: ${loc}` : ''}`;
        } else if (isHospital && clientName) {
          desc = `${clientName}${staffName ? ` — by ${staffName}` : ''}`;
        } else if (isReturnFromHospital && clientName) {
          desc = `${clientName}: Hospital → ${toStatus || '—'}${staffName ? ` — by ${staffName}` : ''}`;
        } else if (isStatusChange && clientName) {
          desc = `${clientName}: ${details.from || '—'} → ${toStatus || '—'}`;
        } else if (actionType === 'room_assignment' && clientName) {
          desc = bedLabel ? `${clientName} → ${bedLabel}` : `${clientName} — Room Unassigned`;
        } else if (actionType === 'update_client' && clientName) {
          const fromRoom = details.from_room || details.previous_room;
          const toRoom = details.to_room || room;
          const fieldList = formatClientFieldList(
            Array.isArray(details.updates) ? details.updates : Array.isArray(details.changed_fields) ? details.changed_fields : []
          );
          const fieldSuffix = fieldList.length > 0 ? ` (${fieldList.slice(0, 3).join(', ')}${fieldList.length > 3 ? `, +${fieldList.length - 3} more` : ''})` : '';
          if (fromRoom && toRoom) desc = `${clientName}: Bed ${fromRoom} → Bed ${toRoom}${fieldSuffix}`;
          else if (toRoom) desc = `${clientName} → Bed ${toRoom}${fieldSuffix}`;
          else desc = `${clientName}${fieldSuffix}`;
        } else if (actionType === 'assign_pass' && clientName) {
          const end = details.pass_end ? format(new Date(details.pass_end), 'MMM dd') : '';
          const ptRaw = (details.pass_type as string | undefined);
          const ptLabel = ptRaw === 'late' ? 'Late Pass'
            : ptRaw === 'overnight' ? 'Overnight Pass'
            : ptRaw === 'multi_use' ? 'Multi-use Pass'
            : 'Pass';
          desc = `${clientName} — ${ptLabel}${end ? ` until ${end}` : ''}`;
        } else if (actionType === 'clear_pass' && clientName) {
          desc = clientName;
        } else if (actionType === 'create_note') {
          const noteTitle = details.note_title || details.title;
          if (noteTitle) title = noteTitle;
          desc = clientName
            ? `${clientName}${staffName ? ` — by ${staffName}` : ''}`
            : (staffName ? `by ${staffName}` : undefined);
        } else if (actionType === 'submit_inspection') {
          desc = details.template_name || details.area || 'Inspection submitted';
        } else if (actionType === 'property_return' && clientName) {
          const loc = details.property_location || details.storage_location;
          desc = `${clientName}${loc ? ` (from ${loc})` : ''}`;
        } else if (actionType === 'archive_client' && clientName) {
          desc = `${clientName}${details.reason ? ` — ${details.reason}` : ''}`;
        } else if (actionType === 'bed_count') {
          const ev = details.event;
          const actor = staffName || details.user || details.full_name;
          if (ev === 'bed_count_signoff') {
            title = 'Bed Count Completed';
            desc = actor ? `Signed off by ${actor}` : 'Signed off';
          } else if (ev === 'bed_count_start') {
            title = 'Bed Count Started';
            const ec = details.entry_count;
            desc = `${actor ? `Started by ${actor}` : 'Started'}${typeof ec === 'number' ? ` — ${ec} clients` : ''}`;
          } else if (ev === 'bed_count_created') {
            return null; // skip noisy "created" events
          } else {
            desc = ev || 'Bed count updated';
          }
        } else if (actionType === 'bed_count_avc_call') {
          title = 'AVC Report Called In';
          desc = staffName ? `Called in by ${staffName}` : 'Called in to AVC';
        } else if (actionType === 'bed_count_waitlist_add') {
          title = 'Added to Daily Waitlist';
          const reasonMap: Record<string, string> = {
            late_return: 'Late Return',
            returnee: 'Returnee',
            overage: 'Overage',
          };
          const r = details.reason ? reasonMap[details.reason] ?? details.reason : '';
          desc = `${details.arrival_name ?? 'Arrival'}${r ? ` — ${r}` : ''}`;
        } else if (actionType === 'bed_count_waitlist_resolve') {
          title = 'Waitlist Updated';
          const resMap: Record<string, string> = {
            rebedded: 'Rebedded',
            transferred: 'Transferred',
            left: 'Left',
            pending: 'Set to Pending',
          };
          desc = details.resolution ? (resMap[details.resolution] ?? details.resolution) : 'Resolution updated';
        }

        // Append "by <Actor>" if we have one and it's not already mentioned
        let finalDesc = desc;
        if (staffName) {
          const already = finalDesc && /\bby\s/i.test(finalDesc);
          if (!already) {
            finalDesc = finalDesc ? `${finalDesc} — by ${staffName}` : `by ${staffName}`;
          }
        }

        let category: string | undefined;
        if (isUnbedded) category = 'unbedded';
        else if (isHospital || isReturnFromHospital) category = 'hospital';
        else if (actionType === 'assign_pass' || actionType === 'clear_pass') category = 'pass';
        else if (actionType === 'create_client') category = 'intake';
        else if (actionType === 'room_assignment' || actionType === 'update_client') category = 'bed';
        else if (actionType === 'property_return') category = 'belongings';
        else if (actionType === 'archive_client') category = 'archive';
        else if (actionType === 'submit_inspection') category = 'inspection';
        else if (actionType === 'client_status_change') category = 'status';

        return {
          id: `act-${log.id}`,
          time: log.created_at!,
          type: 'activity' as const,
          category,
          icon,
          title,
          description: finalDesc || undefined,
          badgeVariant: 'outline' as const,
          createdBy: (log as any).user_id || undefined,
          actorName: staffName,
          caseManagerNotified: isUnbedded,
          clientName: clientName || undefined,
        };
      })
      .filter((e): e is NonNullable<typeof e> => e !== null) as TimelineEvent[],
  ].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  // Dedupe: when a note entry and an activity entry describe the same event
  // (same client + overlapping category, within ~2 min), drop the activity entry
  // because the note carries the richer detail and explicit author attribution.
  const NOTE_CATEGORY_ALIASES: Record<string, string[]> = {
    belongings: ['property', 'property_return', 'unbedded'],
    unbedded: ['property', 'property_return', 'unbedded'],
    archive: ['archive', 'archive_client'],
    intake: ['intake', 'create_client'],
    hospital: ['hospital'],
    pass: ['pass'],
    status: ['status'],
  };
  const noteSignatures = new Map<string, number[]>(); // key: clientName|alias -> timestamps
  timeline.forEach((e) => {
    if (e.type !== 'manual') return;
    const name = (e.clientName || '').toLowerCase().trim();
    if (!name) return;
    const aliases = NOTE_CATEGORY_ALIASES[e.category || ''] || [e.category || 'note'];
    const t = new Date(e.time).getTime();
    aliases.forEach((alias) => {
      const key = `${name}|${alias}`;
      const arr = noteSignatures.get(key) || [];
      arr.push(t);
      noteSignatures.set(key, arr);
    });
  });
  const WINDOW_MS = 2 * 60 * 1000;
  const dedupedTimeline = timeline.filter((e) => {
    if (e.type !== 'activity') return true;
    const name = (e.clientName || '').toLowerCase().trim();
    if (!name || !e.category) return true;
    const key = `${name}|${e.category}`;
    const matches = noteSignatures.get(key);
    if (!matches || matches.length === 0) return true;
    const t = new Date(e.time).getTime();
    return !matches.some((nt) => Math.abs(nt - t) <= WINDOW_MS);
  });

  // Roll-up totals from timeline (by category) for Shift Details
  const totalsCategoryConfig: { key: string; label: string }[] = [
    { key: 'unbedded', label: 'Unbedded' },
    { key: 'note', label: 'Notes / Manual Entries' },
    { key: 'pass', label: 'Passes' },
    { key: 'hospital', label: 'Hospital' },
    { key: 'intake', label: 'New Intakes' },
    { key: 'metro', label: 'Transit / Metro' },
    { key: 'laundry', label: 'Laundry' },
  ];
  const shiftTotals = totalsCategoryConfig.map(({ key, label }) => {
    const matched = dedupedTimeline.filter((e) => e.category === key);
    const names = matched
      .map((e) => e.clientName || e.title)
      .filter(Boolean) as string[];
    return { key, label, count: matched.length, names };
  });

  // Friendly badge label fallback per event
  const badgeLabel = (e: TimelineEvent): string | null => {
    if (e.category === 'bed-count') return null;
    if (e.category) {
      const found = totalsCategoryConfig.find((t) => t.key === e.category);
      if (found) return found.label.split(' / ')[0].toLowerCase();
      return e.category;
    }
    if (e.type === 'manual') return 'note';
    if (e.type === 'alert') return 'alert';
    if (e.type === 'service') return 'service';
    return null; // hide bare "activity"
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <Button variant="ghost" size="sm" onClick={onBack} className="w-fit">
            <Home className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">Shift Report</h1>
            <p className="text-muted-foreground text-sm truncate">
              {shiftLabel} • {format(selectedDate, 'MMMM d, yyyy')} • {dedupedTimeline.length} activity entries
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setShiftDetailsOpen(true)}>
              <ClipboardList className="h-4 w-4 mr-1" /> Details
              <span className="ml-1 text-xs text-muted-foreground">{completedDetailCount}</span>
            </Button>
            <Dialog open={addEntryOpen} onOpenChange={setAddEntryOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" /> Add Entry
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Shift Report Entry</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label>Entry Type</Label>
                    <Select value={entryType} onValueChange={setEntryType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="note">General Note</SelectItem>
                        <SelectItem value="observation">Observation</SelectItem>
                        <SelectItem value="incident">Incident</SelectItem>
                        <SelectItem value="alert">Alert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input type="time" value={entryTime} onChange={(e) => setEntryTime(e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input placeholder="Brief summary..." value={entryTitle} onChange={(e) => setEntryTitle(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea placeholder="Details..." value={entryDescription} onChange={(e) => setEntryDescription(e.target.value)} rows={3} />
                  </div>
                  <Button className="w-full" disabled={!entryTitle.trim() || addEntry.isPending} onClick={() => addEntry.mutate()}>
                    {addEntry.isPending ? 'Saving...' : 'Save Entry'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" size="icon" onClick={handlePrint} aria-label="Print shift report">
              <Printer className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleDownload} aria-label="Download shift report">
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-[220px_minmax(260px,360px)] md:items-center">
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn('w-full justify-start text-left font-normal')}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(selectedDate, 'PPP')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => {
                  if (d) {
                    setSelectedDate(d);
                    setDateOpen(false);
                  }
                }}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>

          <Select value={shift} onValueChange={(value) => setShift(value as ShiftType)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select report view" />
            </SelectTrigger>
            <SelectContent>
              {reportOptions.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {formatShiftLabel(item)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
          {[
            { label: 'Checked In', value: checkedInClients.length },
            { label: 'Not Present', value: notPresentCount },
            { label: 'On Pass', value: onPassNow.length },
            { label: 'Unbedded', value: unbeddedCount },
          ].map((s) => (
            <div key={s.label} className="rounded-md border bg-muted/30 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{s.label}</div>
              <div className="text-2xl font-semibold tabular-nums">{s.value}</div>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">
          Top totals reflect the current facility snapshot. Shift Details totals auto-populate from items distributed during this report window.
        </p>

        <ShiftDetailsDialog
          open={shiftDetailsOpen}
          onOpenChange={setShiftDetailsOpen}
          details={reportDetails}
          template={shiftReportTemplate}
          onSave={saveShiftDetails}
          lockedFields={serviceTotalFieldIds}
          shiftTotals={shiftTotals}
        />
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Shift Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {dedupedTimeline.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No activity recorded for this shift.
            </p>
          ) : (
            <div className="relative space-y-0">
              {/* Vertical line */}
              <div className="absolute left-[19px] top-2 bottom-2 w-px bg-border" />
              {dedupedTimeline.map((event) => (
                <div
                  key={event.id}
                  className="flex items-start gap-4 py-3 relative cursor-pointer hover:bg-muted/50 rounded-lg px-2 -mx-2 transition-colors"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="z-10 flex-shrink-0 h-10 w-10 rounded-full bg-card border flex items-center justify-center">
                    {event.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{event.title}</span>
                      {(() => {
                        const lbl = badgeLabel(event);
                        return lbl ? (
                          <Badge variant={event.badgeVariant} className="text-[10px]">{lbl}</Badge>
                        ) : null;
                      })()}
                    </div>
                    {event.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {formatNoteText(event.description)}
                      </p>
                    )}
                    {event.caseManagerNotified && (
                      <p className="text-[10px] text-warning mt-0.5 font-medium">
                        Case Manager Notified
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(event.time), 'h:mm a')}
                    </span>
                    <Maximize2 className="h-3 w-3 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Entry Detail Dialog */}
      <Dialog open={!!selectedEvent && !editingEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent?.icon}
              {selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2">
                <Badge variant={selectedEvent.badgeVariant} className="text-xs">
                  {selectedEvent.type}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {format(new Date(selectedEvent.time), 'MMMM d, yyyy — h:mm a')}
                </span>
              </div>
              {selectedEvent.description ? (
                <ScrollArea className="max-h-[300px]">
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">
                    {formatNoteText(selectedEvent.description)}
                  </p>
                </ScrollArea>
              ) : (
                <p className="text-sm text-muted-foreground italic">No additional details.</p>
              )}
              {/* Show edit button if user owns the entry OR is manager/admin */}
              {selectedEvent.sourceId && (canEditAll || selectedEvent.createdBy === currentUserId) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditTitle(selectedEvent.title);
                    setEditDescription(selectedEvent.description || '');
                    setEditingEvent(selectedEvent);
                  }}
                >
                  Edit Entry
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editingEvent} onOpenChange={(open) => { if (!open) setEditingEvent(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} rows={4} />
            </div>
            <div className="flex gap-2">
              <Button
                className="flex-1"
                disabled={!editTitle.trim() || editEntry.isPending}
                onClick={() => editingEvent && editEntry.mutate({ event: editingEvent, title: editTitle, description: editDescription })}
              >
                {editEntry.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={() => setEditingEvent(null)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftReport;
